"use client"
import { useState } from "react"
import { useRouter } from "next/navigation"
import { CheckCircle2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ImageUpload } from "@/components/ImageUpload"
import { PhotoGuidelines } from "@/components/PhotoGuidelines"

const DISCIPLINES = [
  "Visual Art", "Writing / Literature", "Music / Sound",
  "Film / Video", "Photography", "Theatre / Performance",
  "Dance", "Architecture / Design", "Interdisciplinary", "Craft / Textiles",
]

const CONTINENTS = [
  "North America", "Europe", "South America",
  "Asia", "Africa", "Oceania", "Middle East",
]

export function SubmitResidencyForm() {
  const router = useRouter()
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  // Form state
  const [name, setName] = useState("")
  const [website, setWebsite] = useState("")
  const [location, setLocation] = useState("")
  const [country, setCountry] = useState("")
  const [continent, setContinent] = useState("")
  const [description, setDescription] = useState("")
  const [disciplines, setDisciplines] = useState<string[]>([])
  const [costType, setCostType] = useState("free")
  const [stipend, setStipend] = useState("")
  const [fee, setFee] = useState("")
  const [costDetails, setCostDetails] = useState("")
  const [artistsAtOnce, setArtistsAtOnce] = useState("")
  const [duration, setDuration] = useState("")
  const [deadline, setDeadline] = useState("")
  const [applicationUrl, setApplicationUrl] = useState("")
  const [housing, setHousing] = useState(false)
  const [meals, setMeals] = useState(false)
  const [studioSpace, setStudioSpace] = useState(false)
  const [openToAll, setOpenToAll] = useState(true)
  const [childcareAvailable, setChildcareAvailable] = useState(false)
  const [familyHousing, setFamilyHousing] = useState(false)
  const [partnerWelcome, setPartnerWelcome] = useState(false)
  const [petFriendly, setPetFriendly] = useState(false)
  const [cancellationPolicy, setCancellationPolicy] = useState("")
  const [imageUrl, setImageUrl] = useState("")
  const [imageSourceUrl, setImageSourceUrl] = useState("")
  const [imageSourceName, setImageSourceName] = useState("")

  function toggleDiscipline(d: string) {
    setDisciplines((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]
    )
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name || !location || !country || !description || disciplines.length === 0) {
      setError("Please fill in all required fields and select at least one discipline.")
      return
    }
    setLoading(true)
    setError("")
    try {
      const res = await fetch("/api/residencies", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name, website, location, country, continent, description,
          disciplines: disciplines.map((d) => d.toLowerCase().replace(/ \/ /g, "-").replace(/ /g, "-")),
          costType, stipend, fee, costDetails, artistsAtOnce,
          duration, deadline, applicationUrl,
          imageUrl, imageSourceUrl, imageSourceName,
          housing, meals, studioSpace, openToAll,
          childcareAvailable, familyHousing, partnerWelcome, petFriendly,
          cancellationPolicy,
        }),
      })
      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error ?? "Submission failed")
      }
      setSubmitted(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong")
    } finally {
      setLoading(false)
    }
  }

  if (submitted) {
    return (
      <div className="max-w-lg mx-auto px-4 py-24 text-center">
        <CheckCircle2 size={52} className="text-emerald-500 mx-auto mb-5" />
        <h1 className="text-2xl font-bold mb-3">Submission received!</h1>
        <p className="text-[var(--muted-foreground)] mb-4">
          Thank you! We review every submission and will approve it within a few days.
        </p>
        <div className="bg-[var(--muted)]/.05 border border-[var(--border)] rounded-lg px-4 py-3 text-sm text-[var(--muted-foreground)] mb-6 text-left">
          <p className="font-medium text-[var(--foreground)] mb-1">What happens next?</p>
          <ul className="space-y-1 list-disc list-inside">
            <li>Your account is active — you can log in anytime</li>
            <li>Your listing will appear in the public directory once approved</li>
            <li>You&apos;ll receive an email when your residency goes live</li>
            <li>Check your <a href="/programs" className="underline text-[var(--primary)]">program dashboard</a> for status updates</li>
          </ul>
        </div>
        <div className="flex gap-3 justify-center">
          <Button onClick={() => router.push("/directory")}>Browse Directory</Button>
          <Button variant="outline" onClick={() => { setSubmitted(false); setName(""); setDescription(""); setDisciplines([]) }}>
            Submit Another
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Add a Residency Program</h1>
        <p className="text-[var(--muted-foreground)]">
          This form lists a residency <em>program</em> in the RMAR directory — for the
          organizations and hosts that run residencies, or anyone recommending a program
          that isn&apos;t in our directory yet. We review every listing before it goes live,
          usually within 2–3 days.
        </p>
      </div>

      {/* Artist redirect — this form is not an application */}
      <div className="mb-8 rounded-lg border border-[var(--border)] bg-[var(--muted)]/[.04] px-4 py-4">
        <p className="text-sm font-medium text-[var(--foreground)] mb-1">
          Are you an artist looking <em>for</em> a residency?
        </p>
        <p className="text-sm text-[var(--muted-foreground)] mb-3">
          This isn&apos;t an application or a place to post your portfolio. To find and apply
          to residencies, browse open calls and the directory instead.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button asChild variant="outline" size="sm">
            <a href="/">Browse open calls</a>
          </Button>
          <Button asChild variant="outline" size="sm">
            <a href="/directory">Explore the directory</a>
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-7">
        {/* Basic info */}
        <section className="space-y-4">
          <h2 className="text-lg font-semibold border-b border-[var(--border)] pb-2">Basic Information</h2>

          <div>
            <label className="text-sm font-medium block mb-1">Residency Name <span className="text-red-500">*</span></label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. MacDowell" required />
          </div>

          <div>
            <label className="text-sm font-medium block mb-1">Website</label>
            <Input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://..." type="url" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium block mb-1">City / Location <span className="text-red-500">*</span></label>
              <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Peterborough, NH" required />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Country <span className="text-red-500">*</span></label>
              <Input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="e.g. United States" required />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium block mb-1">Continent</label>
            <select
              value={continent}
              onChange={(e) => setContinent(e.target.value)}
              className="w-full rounded-[var(--radius)] border border-[var(--input)] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--ring)]"
            >
              <option value="">Select...</option>
              {CONTINENTS.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium block mb-1">Description <span className="text-red-500">*</span></label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the residency — its focus, environment, what makes it special..."
              rows={5}
              required
              className="w-full rounded-[var(--radius)] border border-[var(--input)] bg-white px-3 py-2 text-sm placeholder:text-[var(--muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)] resize-y"
            />
          </div>
        </section>

        {/* Disciplines */}
        <section className="space-y-3">
          <h2 className="text-lg font-semibold border-b border-[var(--border)] pb-2">
            Disciplines <span className="text-red-500">*</span>
          </h2>
          <p className="text-xs text-[var(--muted-foreground)]">Select all that apply.</p>
          <div className="flex flex-wrap gap-2">
            {DISCIPLINES.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => toggleDiscipline(d)}
                className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                  disciplines.includes(d)
                    ? "bg-[var(--accent)] text-white border-[var(--accent)]"
                    : "border-[var(--border)] hover:border-[var(--accent)] text-[var(--foreground)]"
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </section>

        {/* Cost */}
        <section className="space-y-4">
          <h2 className="text-lg font-semibold border-b border-[var(--border)] pb-2">Cost & Support</h2>

          <div>
            <label className="text-sm font-medium block mb-2">Cost Type</label>
            <div className="flex flex-wrap gap-2">
              {[
                { value: "free", label: "Free" },
                { value: "stipend", label: "Paid stipend" },
                { value: "fee", label: "Application / program fee" },
                { value: "self-funded", label: "Self-funded" },
              ].map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setCostType(value)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                    costType === value
                      ? "bg-[var(--primary)] text-white border-[var(--primary)]"
                      : "border-[var(--border)] hover:border-[var(--primary)]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {costType === "stipend" && (
            <div>
              <label className="text-sm font-medium block mb-1">Stipend amount (optional)</label>
              <Input value={stipend} onChange={(e) => setStipend(e.target.value)} placeholder="e.g. $750/month" />
            </div>
          )}
          {costType === "fee" && (
            <div>
              <label className="text-sm font-medium block mb-1">Fee amount (optional)</label>
              <Input value={fee} onChange={(e) => setFee(e.target.value)} placeholder="e.g. $50 application fee" />
            </div>
          )}

          <div>
            <label className="text-sm font-medium block mb-1">Cost details (optional)</label>
            <textarea
              value={costDetails}
              onChange={(e) => setCostDetails(e.target.value)}
              rows={3}
              placeholder="Anything that doesn't fit a single number — e.g. €1,000/month without accommodation or €1,850/month with; duo artists +€150; materials included"
              className="w-full border border-[var(--border)] rounded-[var(--radius)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
            />
            <p className="text-xs text-[var(--muted-foreground)] mt-1">
              Optional add-ons, tiered pricing, surcharges, or what the cost includes. Shown on your public listing.
            </p>
          </div>

          <div className="max-w-xs">
            <label className="text-sm font-medium block mb-1">Artists in residence at a time (optional)</label>
            <Input value={artistsAtOnce} onChange={(e) => setArtistsAtOnce(e.target.value)} placeholder='e.g. "1 (solo)" or "6-8"' />
            <p className="text-xs text-[var(--muted-foreground)] mt-1">How many artists share the residency during the same period?</p>
          </div>

          <div className="flex flex-wrap gap-6">
            {[
              { label: "Housing provided", value: housing, set: setHousing },
              { label: "Meals provided", value: meals, set: setMeals },
              { label: "Studio / workspace", value: studioSpace, set: setStudioSpace },
              { label: "Open to all artists", value: openToAll, set: setOpenToAll },
            ].map(({ label, value, set }) => (
              <label key={label} className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={value}
                  onChange={(e) => set(e.target.checked)}
                  className="rounded"
                />
                {label}
              </label>
            ))}
          </div>
          <p className="text-xs text-[var(--muted-foreground)] mt-1">Caregiver-friendly</p>
          <div className="flex flex-wrap gap-6">
            {[
              { label: "Childcare available", value: childcareAvailable, set: setChildcareAvailable },
              { label: "Family housing", value: familyHousing, set: setFamilyHousing },
              { label: "Partners welcome", value: partnerWelcome, set: setPartnerWelcome },
              { label: "Pet-friendly", value: petFriendly, set: setPetFriendly },
            ].map(({ label, value, set }) => (
              <label key={label} className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={value}
                  onChange={(e) => set(e.target.checked)}
                  className="rounded"
                />
                {label}
              </label>
            ))}
          </div>
          <div className="mt-4 max-w-xs">
            <label className="block text-sm font-medium mb-1">Cancellation / refund policy</label>
            <select
              value={cancellationPolicy}
              onChange={(e) => setCancellationPolicy(e.target.value)}
              className="w-full border border-[var(--border)] rounded-[var(--radius)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
            >
              <option value="">Not specified</option>
              <option value="full_refund">Full refund</option>
              <option value="partial_refund">Partial refund</option>
              <option value="credit_only">Credit / reschedule only</option>
              <option value="none">No refund</option>
              <option value="not_applicable">Not applicable (no fees)</option>
            </select>
            <p className="text-xs text-[var(--muted-foreground)] mt-1">If a resident cancels or the program is cancelled, what happens to fees paid?</p>
          </div>
        </section>

        {/* Cover Image */}
        <section className="space-y-4">
          <h2 className="text-lg font-semibold border-b border-[var(--border)] pb-2">Cover Image</h2>
          <p className="text-sm text-[var(--muted-foreground)]">
            Add a photo that represents this residency. Listings with quality images are
            approved faster and get more attention from the community.
          </p>
          <PhotoGuidelines />
          <ImageUpload
            imageUrl={imageUrl}
            imageSourceUrl={imageSourceUrl}
            imageSourceName={imageSourceName}
            onImageChange={setImageUrl}
            onSourceUrlChange={setImageSourceUrl}
            onSourceNameChange={setImageSourceName}
            sourceNameLabel="Image credit / source"
            requireLicenseAgreement
          />
        </section>

        {/* Application */}
        <section className="space-y-4">
          <h2 className="text-lg font-semibold border-b border-[var(--border)] pb-2">Application Details</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium block mb-1">Duration</label>
              <Input value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="e.g. 2–8 weeks" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Application Deadline</label>
              <Input value={deadline} onChange={(e) => setDeadline(e.target.value)} placeholder="e.g. Jan 15 annually" />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium block mb-1">Application URL</label>
            <Input value={applicationUrl} onChange={(e) => setApplicationUrl(e.target.value)} placeholder="https://..." type="url" />
          </div>
        </section>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex items-center gap-4 pt-2">
          <Button type="submit" size="lg" disabled={loading}>
            {loading ? "Submitting..." : "Submit for Review"}
          </Button>
          <p className="text-xs text-[var(--muted-foreground)]">
            Submissions are reviewed before going live.
          </p>
        </div>
      </form>
    </div>
  )
}
